
import React, { useEffect, useState } from "react";
import { getTags } from "../../../services/analytics/analyticsService";
import { TagsResponse } from "../../../interfaces/analytics";
import AnalyticsFilters from "./AnalyticsFilters";
import Pagination from "../utility/Pagination";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line } from 'recharts';
import { useAuth } from "../../../firebase/AuthProvider";

const TagsAnalytics: React.FC = () => {
  const { currentUser } = useAuth();
  const [authToken, setAuthToken] = useState<string>("");
  const [data, setData] = useState<TagsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (currentUser) {
      currentUser.getIdToken().then(token => setAuthToken(token));
    }
  }, [currentUser]);

  useEffect(() => {
    if (!authToken) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getTags(authToken, filters);
        setData(response);
        setCurrentPage(1);
      } catch (err) {
        setError("Failed to fetch Tags data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authToken, filters]);

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data ? data.rows.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = data ? Math.ceil(data.rows.length / itemsPerPage) : 0;

  if (loading) {
    return <div className="text-center p-4">Loading Tags data...</div>;
  }

  if (error) {
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Tags Analytics</h2>
      <AnalyticsFilters filters={filters} onFiltersChange={handleFiltersChange} />
      {data ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={data.rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="tagName" />
              <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
              <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="taskCount" fill="#8884d8" name="Task Count" />
              <Line yAxisId="right" type="monotone" dataKey="onTimePct" stroke="#82ca9d" name="On Time %" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Tag Name</th>
                  <th className="py-2 px-4 border-b">Task Count</th>
                  <th className="py-2 px-4 border-b">On Time %</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((row, index) => (
                  <tr key={index}>
                    <td className="py-2 px-4 border-b">{row.tagName}</td>
                    <td className="py-2 px-4 border-b">{row.taskCount}</td>
                    <td className="py-2 px-4 border-b">{row.onTimePct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <div className="text-center p-4">No Tags data available.</div>
      )}
    </div>
  );
};

export default TagsAnalytics;
